/**
 * @module account.actions
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { setFeatureFlagUser } from '@worktango/feature-flags-react';
import { client as apollo } from '~/adapters/worktango/apollo';
import * as tokens from '~/adapters/tokens';
import * as localstorage from '~/adapters/localstorage';
import * as deepState from '~/adapters/deep_state';
import * as pendo from '~/adapters/pendo';
import { graphqlRequest, hasGraphQLErrors, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import { httpRequest } from '~/adapters/httpRequest';
import {
  ActivateUserDocument,
  ForgotPasswordDocument,
  GetActivationUserDocument,
  LoginDocument,
  LogoutDocument,
  SetNewPasswordWithTokenDocument,
  SwitchCompanyDocument,
} from '~/gql/graphql';
import { handleUnsafeOptionalChaining } from '~/tools/lint/handleUnsafeOptionalChaining';
import { appQueryClient } from '~/adapters/reactQuery';
import { shutdownZendeskWidget, startZendeskWidget } from '~/adapters/zendesk';
import { errors } from '~/tools/errors';
import { userActions } from './user.actions';
import { languageActions } from './language.actions';
import { employeeActions } from './employee.actions';
import { surveyTypeActions } from './survey_type.actions';
import * as deepStateSettings from '~/config/settings/deep_state.settings';
import * as schemas from '../schemas';
import { fetchCurrentAccountQuery, accountKeys } from '../react-query/account';
import { setUser } from '~/telemetry/datadog_tracer';

/**
 * Fetch the account, permissions, and company data for the current account user.
 *
 * REFACTOR: Ultimately we would like there to be a single network request to
 * fetch all "account" data at once (e.g., using this new /users/me endpoint).
 * But, for now, we need to make 2 separate network requests until the new
 * endpoint can fulfill all the necessary response data currently in the legacy
 * GraphQL user endpoint. But the meaning is the same: to get all account data.
 */
// @ts-expect-error TS(2339): Property 'userId' does not exist on type 'void'.
const fetchAccount = createAsyncThunk('account/fetchAccount', async () => {
  const { account, company, permissions } = await fetchCurrentAccountQuery();

  const normalizedData = normalize(company, schemas.company);
  return {
    account,
    permissions,
    ...normalizedData,
  };
});

/**
 * Takes a `token` and stores it in the token store, then executes a series
 * of initialization functions necessary before starting the main app.
 */
const init = createAsyncThunk('account/init', async (token = '', { dispatch, rejectWithValue, getState }) => {
  if (!token) {
    return rejectWithValue({
      message: 'Unable to find valid access token; please login',
      token,
    });
  }

  // Store access token using token adapter.
  tokens.saveAccessToken(token);

  const accountAction = await dispatch(fetchAccount());
  const { account } = accountAction.payload;
  const { permissions } = getState();

  const salesforceId = accountAction?.payload?.entities?.companies[account?.companyId]?.salesforceId;
  const initRequests = [];
  // Trigger language change in case account data has been updated since the
  // account was last fetched.
  initRequests.push(dispatch(userActions.changeLanguage(account.lang)));

  // Fetch all language data from the server.
  initRequests.push(dispatch(languageActions.fetchAllLanguages()));

  // Fetch company's survey types + categories.
  initRequests.push(
    dispatch(
      surveyTypeActions.fetchSurveyTypesWithCategories({
        companyId: account.companyId,
        lang: account.lang,
      }),
    ),
  );

  // Get current user's related employee record.
  if (account.employeeId) {
    initRequests.push(
      dispatch(
        employeeActions.fetchEmployee({
          companyId: account.companyId,
          employeeId: account.employeeId,
        }),
      ),
    );
  }

  // The errors were already silent before, but comment is just a warning for future devs
  // The requests don't return a reject, they resolve with the type reject. So they silently fail.
  await Promise.all(initRequests);

  // Initialize Big State (IndexedDB).
  await deepState.init(deepStateSettings.version, deepStateSettings.schema);

  // Set feature flag user.
  await setFeatureFlagUser({
    user_id: account.email,
    email: account.email,
    privateCustomData: {
      worktangoAdmin: account.isAdmin,
      companyId: account.companyId,
    },
  });

  // Identify the user for telemetry services
  setUser({
    email: account.email,
    companyId: account.companyId,
  });

  const pendoInit = {
    ...account,
    salesforceId,
    permissions,
  };

  pendo.initializePendo(pendoInit);

  startZendeskWidget();
});

/**
 * Attempt to load an access token from the token store and initialize the app.
 */
const restoreAccount = createAsyncThunk('account/restore', async (_, { dispatch }) => {
  const token = tokens.getAccessToken();

  // Only invalidate the account query because this action is also called when
  // we create/update a survey type
  appQueryClient.invalidateQueries(accountKeys.fetchCurrentAccount());

  await dispatch(init(token));
});

const checkSSO = createAsyncThunk('account/checkSSO', async (email) => {
  const res = await httpRequest(`/users/${email}/sso`, { skipAuth: true });

  return res?.payload?.sso?.redirectUrl;
});

/**
 * Send the client ID and ID token to the server and get back an access token.
 * Initialize the app with the provided token.
 */
const loginWithSSO = createAsyncThunk(
  'account/loginWithSSO',

  async ({ clientId, ssoToken }, { dispatch, rejectWithValue }) => {
    const res = await httpRequest(`/login/sso/${clientId}/${ssoToken}`, { skipAuth: true });

    if (res.error) return rejectWithValue(res.error);

    const { token } = handleUnsafeOptionalChaining(res?.payload);

    await dispatch(init(token));
  },
);

/**
 * Send email + password to server and get back an access token.
 * Initialize the app with the provided token.
 */
const loginWithPassword = createAsyncThunk(
  'account/loginWithPassword',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const res = await graphqlRequest(LoginDocument, { email, password });

      if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
      if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

      const { token } = handleUnsafeOptionalChaining(res?.data?.login);

      await dispatch(init(token));
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

/**
 * Change the account user's company (if they have admin permissions).
 * If successful perform a fresh account restore.
 */
const switchCompany = createAsyncThunk('account/switchCompany', async (companyId, { dispatch, rejectWithValue }) => {
  const res = await graphqlRequest(SwitchCompanyDocument, { companyId });

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

  const { token } = handleUnsafeOptionalChaining(res?.data?.switchAccount);

  appQueryClient.invalidateQueries(accountKeys.fetchCurrentAccount());

  await dispatch(init(token));
});

/**
 * Tell the server that the user has logged out.
 * Execute app shutdown functions.
 */
const logout = createAsyncThunk('account/logout', async (error) => {
  try {
    await graphqlRequest(LogoutDocument);
  } catch (err) {
    // Do nothing.
  } finally {
    // Ensure that localstorage is cleared regardless of if API request fails.
    tokens.clear();
    appQueryClient.clear();

    shutdownZendeskWidget();

    await localstorage.clearStore();
    await apollo.clearStore();
  }

  // If an error is passed to the action, store its value in the ui.error state,
  // so it can be picked up (e.g., on the login page as a notification message).
  return {
    logoutError: {
      // @ts-expect-error TS(2339): Property 'name' does not exist on type 'void'.
      name: error.name,
      // @ts-expect-error TS(2339): Property 'message' does not exist on type 'void'.
      message: error.message,
    },
  };
});

/**
 * Activate user with name + password.
 */
const activateUser = createAsyncThunk(
  'account/activateUser',

  async ({ activationCode, firstName, lastName, password }, { dispatch }) => {
    try {
      await dispatch(logout());
    } finally {
      await graphqlRequest(ActivateUserDocument, {
        token: activationCode,
        user: {
          firstName,
          lastName,
          password,
        },
      });
    }

    return true;
  },
);

const forgotPassword = createAsyncThunk('account/forgotPassword', async (email) => {
  const res = await graphqlRequest(ForgotPasswordDocument, { email });

  return res.data?.forgotPassword;
});

const resetPassword = createAsyncThunk('account/resetPassword', async ({ resetToken, newPassword }) => {
  await graphqlRequest(SetNewPasswordWithTokenDocument, {
    token: resetToken,
    password: newPassword,
  });
});

/**
 * Fetch `email` + `companyName` based on `activationCode`.
 */
const getActivationDetails = createAsyncThunk(
  'account/getActivationDetails',
  async (activationCode, { dispatch, getState, rejectWithValue }) => {
    const res = await graphqlRequest(GetActivationUserDocument, {
      token: activationCode,
    });

    // NOTE: bug fix (not just refactor) — previous check used `res?.activationUser`,
    // but the actual response field is `getActivationUser` (see destructure below).
    if (hasGraphQLErrors(res) && res.errors.length > 0 && !res.data?.getActivationUser) {
      return rejectWithValue(errors.types.activationError);
    }

    const { getActivationUser: profile } = res.data ?? {};

    const { account } = getState();

    if (profile.langISOCode && account?.lang !== profile.langISOCode) {
      dispatch(userActions.changeLanguage(profile.langISOCode));
    }

    return {
      email: profile.email,
      companyName: profile.companyName,
      hasSSO: profile.hasSSO,
    };
  },
);

export const accountActions = {
  activateUser,
  checkSSO,
  fetchAccount,
  forgotPassword,
  getActivationDetails,
  init,
  loginWithPassword,
  loginWithSSO,
  logout,
  resetPassword,
  restoreAccount,
  switchCompany,
};
